import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { AuthShell } from '@/components/AuthShell'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { LegalConsent } from '@/components/billing/LegalConsent'
import { VenueFields, resolvedVenue, useSignupTimezones, type FirstVenueDraft } from '@/components/billing/VenueFields'
import { dollars } from '@/lib/billing'
import { LEGAL_PUBLISHED, LEGAL_VERSIONS } from '@lib/constants/legal'
import { PRICE_PER_VENUE_CENTS, TRIAL_DAYS } from '@lib/constants/billing'
import { signupInputSchema } from '@lib/input/signup.input'

const SIGNUP_ERROR_FIELDS = [
  'companyName',
  'name',
  'email',
  'password',
  'legal',
  'venue.name',
  'venue.address',
  'venue.timezone',
] as const

type SignupErrorField = (typeof SIGNUP_ERROR_FIELDS)[number]
type FieldErrors = Partial<Record<SignupErrorField, string>>

function isSignupErrorField(value: string): value is SignupErrorField {
  for (const field of SIGNUP_ERROR_FIELDS) {
    if (field === value) return true
  }
  return false
}

/** Zod issues keyed by field path — `venue.address` for the nested venue. */
function errorsFrom(issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey>; message: string }>): FieldErrors {
  const errors: FieldErrors = {}
  for (const issue of issues) {
    const key = issue.path.map(String).join('.')
    if (!isSignupErrorField(key)) continue
    errors[key] = issue.message
  }
  return errors
}

/**
 * Self-serve signup: a venue company signs itself up, then goes to Stripe for
 * the card that starts its free trial. Public.
 */
export function SignupPage() {
  // Closed until the terms and privacy policy are published: the API refuses a
  // signup until then, so the form is not offered.
  if (!LEGAL_PUBLISHED) return <SignupClosed />
  return <SignupForm />
}

/** What `/signup` says until the terms and privacy policy are published. */
function SignupClosed() {
  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-display">Signup opens soon</CardTitle>
          <CardDescription>FLOH Venues isn’t taking new sign-ups yet.</CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/login" className="text-sm underline underline-offset-2">
            Sign in
          </Link>
        </CardContent>
      </Card>
    </AuthShell>
  )
}

function SignupForm() {
  const { signup } = useAuth()
  const navigate = useNavigate()
  const offered = useSignupTimezones()
  const [companyName, setCompanyName] = React.useState('')
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [venue, setVenue] = React.useState<FirstVenueDraft>({ name: '', address: '', timezone: '' })
  const [agreed, setAgreed] = React.useState(false)
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setSubmitError(null)
    if (!agreed) {
      setErrors({ legal: 'Agree to the terms and privacy policy to continue.' })
      return
    }
    const parsed = signupInputSchema.safeParse({
      companyName,
      name,
      email,
      password,
      venue: resolvedVenue(venue, offered),
      legal: { termsVersion: LEGAL_VERSIONS.terms, privacyVersion: LEGAL_VERSIONS.privacy },
    })
    if (!parsed.success) {
      setErrors(errorsFrom(parsed.error.issues))
      return
    }
    setErrors({})
    setSubmitting(true)
    try {
      await signup(parsed.data)
      // The company exists and is waiting for its card; billing is the one place
      // an unfinished signup can go, and it offers "Continue to payment".
      navigate('/billing', { replace: true })
    } catch (e) {
      setSubmitError(requestErrorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  const venueErrors = {
    ...(errors['venue.name'] ? { name: errors['venue.name'] } : {}),
    ...(errors['venue.address'] ? { address: errors['venue.address'] } : {}),
    ...(errors['venue.timezone'] ? { timezone: errors['venue.timezone'] } : {}),
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-display">Start your free trial</CardTitle>
          <CardDescription>
            {TRIAL_DAYS} days free, then {dollars(PRICE_PER_VENUE_CENTS)} per venue per month. A card is needed to
            start; you won’t be charged until the trial ends.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className="space-y-3">
              <Field label="Company name" htmlFor="company-name" error={errors['companyName']}>
                <Input id="company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
              </Field>
              <Field label="Your name" htmlFor="your-name" error={errors['name']}>
                <Input id="your-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </Field>
              <Field label="Email" htmlFor="email" error={errors['email']}>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
              </Field>
              <Field label="Password" htmlFor="password" error={errors['password']}>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
            </div>
            <VenueFields value={venue} onChange={setVenue} errors={venueErrors} offered={offered} />
            <div className="space-y-1">
              <LegalConsent checked={agreed} onChange={setAgreed} />
              {errors['legal'] ? <FormError>{errors['legal']}</FormError> : null}
            </div>
            {submitError ? <FormError>{submitError}</FormError> : null}
            <Button pending={submitting} type="submit" className="w-full">
              Continue to payment
            </Button>
            <p className="text-center text-sm text-ink-muted">
              Already have an account?{' '}
              <Link to="/login" className="underline underline-offset-2">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  )
}
